import { EllipsisTooltip } from 'components/misc/EllipsisTooltip';
import Loader from 'components/Loader';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { getFilterOptionsFromClassifier } from 'components/tables/list-table/filterUtil';
import { FilterConfig, MainFilter } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { useMemo } from 'react';
import { useLazyGetDraftInvoicesByCaseReportQuery } from 'store/api/reportingFinance';
import { ClassifierData } from 'types/classifiers';
import { DraftInvoicesByCaseRow, DraftInvoicesByCaseTotals } from 'types/reporting/finance';
import { ReportPage } from 'types/reporting/reportPage';
import { t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';
import {
    ReportCaseLink,
    ReportContactLink,
    ReportNumberCell,
    TONED_COLUMN_META,
    tonedCell,
    GREEN_OR_RED,
} from '../reportCells';
import {
    caseFilter,
    clientFilter,
    employeeFilter,
    inStatusOrder,
    quickFiltersFrom,
    searchSelectors,
} from '../reportFilters';
import { inBaseCurrency, rowCurrencies, useReportBaseCurrency } from '../reportCurrency';
import { ReportLayout } from '../ReportLayout';
import { toneLegendOf } from '../ReportToneLegend';
import { ReportRowLimitNote } from '../ReportRowLimitNote';
import { ReportEntry } from '../reportCatalog';
import { ReportInvoiceLink } from './financeReportColumns';

type Row = DraftInvoicesByCaseRow;
type Totals = DraftInvoicesByCaseTotals;
type AmountKey =
    | 'billableHours'
    | 'billableAmountOriginalCurrency'
    | 'billableAmountInvoiceCurrency'
    | 'expensesAmountInvoiceCurrency'
    | 'otherAmountInvoiceCurrency'
    | 'billableAmountBaseCurrency'
    | 'expensesAmountBaseCurrency'
    | 'otherAmountBaseCurrency';
type TotalsKey = Exclude<keyof Totals, 'baseCurrencyCode'>;

const REPORT_KEY = 'draft-invoices-by-case';
const CLASSIFIER_CODES = ['INVOICE_STATUS_TYPE'];
/** The statuses an invoice passes through before it is final, in that order: the only ones the report selects. */
const DRAFT_STATUSES = ['DRAFT', 'REVIEWED', 'APPROVED', 'IN_POSTPONAL'];

function buildFilterConfig(statuses: ClassifierData | undefined): FilterConfig {
    const filters: MainFilter[] = [
        employeeFilter('supervisingPartnerId', 'reporting.filter.supervising-partner'),
        clientFilter(),
        caseFilter(),
        {
            type: 'multiselect',
            label: t('reporting.filter.invoice-status'),
            selector: 'invoiceStatus',
            options: inStatusOrder(
                getFilterOptionsFromClassifier(statuses).filter((option) => DRAFT_STATUSES.includes(option.value)),
                DRAFT_STATUSES
            ),
        },
    ];
    return {
        hideFilterDividers: true,
        quickFilterChips: true,
        quickFilters: quickFiltersFrom(filters, { invoiceStatus: 'reporting.filter.invoice-status' }),
        searchBarSelectors: searchSelectors('invoiceNumber', 'clientName', 'caseNumber', 'caseName'),
        filters,
    };
}

function amountColumn(
    rows: Row[] | undefined,
    id: AmountKey,
    header: string,
    totalsKey?: TotalsKey
): DataTableColumnDef<Row, Totals> {
    return {
        id,
        accessorKey: id,
        header,
        meta: {
            ...TONED_COLUMN_META,
            widthWeight: 0.8,
            minPx: 130,
            textAlign: 'right',
            renderTotal: totalsKey ? (totals) => <ReportNumberCell value={totals?.[totalsKey]} /> : undefined,
        },
        cell: tonedCell(rows, (row) => row[id], GREEN_OR_RED),
    };
}

/** A column naming each row's currency; the totals row lists the currencies of the rows. */
function currencyColumn(
    rows: Row[] | undefined,
    id: 'originalCurrencyCode' | 'invoiceCurrencyCode',
    labelId: string
): DataTableColumnDef<Row, Totals> {
    return {
        id,
        accessorKey: id,
        header: t(labelId),
        enableSorting: false,
        meta: {
            fixedPx: 130,
            renderTotal: () => <EllipsisTooltip>{rowCurrencies(rows, (row) => row[id])}</EllipsisTooltip>,
        },
    };
}

function buildColumns(rows: Row[] | undefined, currency: string | null): DataTableColumnDef<Row, Totals>[] {
    const inBase = (labelId: string) => inBaseCurrency(t(labelId), currency);
    return [
        {
            id: 'invoiceNumber',
            accessorKey: 'invoiceNumber',
            header: t('reporting.column.invoice-number'),
            meta: { widthWeight: 0.6, minPx: 110 },
            cell: ({ row }) => (
                <ReportInvoiceLink invoiceId={row.original.invoiceId}>{row.original.invoiceNumber}</ReportInvoiceLink>
            ),
        },
        {
            id: 'invoiceStatus',
            accessorKey: 'invoiceStatus',
            header: t('reporting.column.invoice-status'),
            meta: { widthWeight: 0.6, minPx: 110 },
        },
        {
            id: 'clientNumber',
            accessorKey: 'clientNumber',
            header: t('reporting.column.client-number'),
            meta: { widthWeight: 0.5, minPx: 90 },
            cell: ({ row }) => (
                <ReportContactLink partyId={row.original.clientId}>{row.original.clientNumber}</ReportContactLink>
            ),
        },
        {
            id: 'clientName',
            accessorKey: 'clientName',
            header: t('reporting.column.client-name'),
            meta: { widthWeight: 1.2, minPx: 160 },
            cell: ({ row }) => (
                <ReportContactLink partyId={row.original.clientId}>{row.original.clientName}</ReportContactLink>
            ),
        },
        {
            id: 'caseNumber',
            accessorKey: 'caseNumber',
            header: t('reporting.column.case-number'),
            meta: { widthWeight: 0.6, minPx: 110 },
            cell: ({ row }) => (
                <ReportCaseLink matterId={row.original.matterId} wrapLabel>
                    {row.original.caseNumber}
                </ReportCaseLink>
            ),
        },
        {
            id: 'caseName',
            accessorKey: 'caseName',
            header: t('reporting.column.case-name'),
            meta: { widthWeight: 1.4, minPx: 180 },
            cell: ({ row }) => (
                <ReportCaseLink matterId={row.original.matterId}>{row.original.caseName}</ReportCaseLink>
            ),
        },
        amountColumn(rows, 'billableHours', t('reporting.column.billable-hours'), 'billableHours'),
        amountColumn(rows, 'billableAmountOriginalCurrency', t('reporting.column.billable-amount-cc')),
        currencyColumn(rows, 'originalCurrencyCode', 'reporting.column.case-currency'),
        amountColumn(rows, 'billableAmountInvoiceCurrency', t('reporting.column.billable-amount-ic')),
        amountColumn(rows, 'expensesAmountInvoiceCurrency', t('reporting.column.expenses-ic')),
        amountColumn(rows, 'otherAmountInvoiceCurrency', t('reporting.column.other-ic')),
        currencyColumn(rows, 'invoiceCurrencyCode', 'reporting.column.invoice-currency'),
        amountColumn(
            rows,
            'billableAmountBaseCurrency',
            inBase('reporting.column.billable-amount'),
            'billableAmountBaseCurrency'
        ),
        amountColumn(
            rows,
            'expensesAmountBaseCurrency',
            inBase('reporting.column.expenses'),
            'expensesAmountBaseCurrency'
        ),
        amountColumn(rows, 'otherAmountBaseCurrency', inBase('reporting.column.other'), 'otherAmountBaseCurrency'),
    ];
}

export function DraftInvoicesByCaseReport({ report }: { report: ReportEntry }) {
    const [fetchData, { data, isFetching, isError }] = useLazyGetDraftInvoicesByCaseReportQuery();
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: CLASSIFIER_CODES });
    const statuses = classifiers['INVOICE_STATUS_TYPE'];
    const filterConfig = useMemo(() => buildFilterConfig(statuses), [statuses]);
    const currency = useReportBaseCurrency(data?.totals?.baseCurrencyCode ?? data?.content[0]?.baseCurrencyCode);
    const columns = useMemo(() => buildColumns(data?.content, currency), [data, currency]);
    return (
        <ReportLayout report={report} fillHeight toneLegend={toneLegendOf(columns)} baseCurrency={currency}>
            {/* The list reads its quick filters' options once, when it mounts. */}
            {isLoadingClassifiers ? (
                <Loader />
            ) : (
                <ListTableWithFilters<Row, ReportPage<Row, Totals>>
                    data={data}
                    belowQuickFilters={<ReportRowLimitNote report={report} rowCount={data?.content.length} />}
                    isLoading={isFetching}
                    isError={isError}
                    fetchData={fetchData}
                    filterConfig={filterConfig}
                    columns={columns}
                    columnConfigId={`report-${REPORT_KEY}`}
                    enableColumnPicker
                    enableColumnReorder
                    enableColumnResize
                    pluralTextId="reporting.rows"
                    singularTextId="reporting.row"
                    noEntriesId="reporting.no-rows"
                    noEntriesBodyId="reporting.no-rows-body"
                />
            )}
        </ReportLayout>
    );
}
